import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  List,
  ListItem,
  Chip,
  Divider
} from '@mui/material';
import { 
  PersonAdd as UserPlusIcon, 
  Business as Building2Icon, 
  CalendarMonth as CalendarIcon, 
  Email as MailIcon, 
  ArrowForward as ArrowRightIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import Container from '../../base/Container';

// FeatureCard Component
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  steps: string[];
  tips: string[];
  color: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, steps, tips, color }) => {
  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3, 
        mb: 2,
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: `${color}15`,
            borderRadius: 2,
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 56,
            minHeight: 56
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b', fontSize: '1.125rem' }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
            {description}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Steps */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 700, 
            mb: 2, 
            color: '#1e293b',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          How to use:
        </Typography>
        <List sx={{ p: 0 }}>
          {steps.map((step, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                px: 0, 
                py: 1.5,
                alignItems: 'flex-start'
              }}
            >
              <Box 
                sx={{ 
                  minWidth: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: '#1976d2',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  mr: 2,
                  flexShrink: 0
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, pt: 0.5 }}>
                {step}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Tips */}
      <Box 
        sx={{ 
          bgcolor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 2,
          p: 2.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534', fontSize: '0.875rem' }}>
            Pro Tips
          </Typography>
        </Box>
        <List sx={{ p: 0, m: 0 }}>
          {tips.map((tip, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                px: 0, 
                py: 0.75, 
                display: 'flex', 
                alignItems: 'flex-start' 
              }}
            >
              <Box 
                sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  bgcolor: '#16a34a',
                  mt: 1,
                  mr: 1.5,
                  flexShrink: 0
                }} 
              />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#166534', lineHeight: 1.6 }}>
                {tip}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
  );
};

// Main Manual Component
const Manual: React.FC = () => {
  const handleContactSupport = () => {
    window.location.href = '/helpcenter';
  };

  return (
    <Container>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
        {/* Header - Matching Client Management style: no border-bottom card, plain background */}
        <Box 
          sx={{ 
            bgcolor: '#f8fafc',
            px: 3,
            pt: 3,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: '#1e293b',
              fontSize: '1.6rem',
              letterSpacing: '-0.01em'
            }}
          >
            User Manual
          </Typography>
          <Button 
            variant="contained" 
            size="medium"
            onClick={handleContactSupport}
            startIcon={<ArrowRightIcon />}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              },
              borderRadius: 1.5,
              fontSize: '0.875rem'
            }}
          >
            Contact Support
          </Button>
        </Box>

        {/* Main Content */}
        <Box sx={{ px: 3, pb: 4 }}>
          {/* Introduction */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 3,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              bgcolor: 'white'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b', fontSize: '1rem' }}>
              Getting Started
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.7, mb: 2.5 }}>
              This manual will guide you through the main features of our application. 
              Each section below provides detailed instructions on how to use each feature effectively.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip 
                label="4 Main Features" 
                size="small"
                sx={{ 
                  bgcolor: '#dbeafe', 
                  color: '#1e40af',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }} 
              />
              <Chip 
                label="Step-by-step guides" 
                size="small"
                sx={{ 
                  bgcolor: '#dbeafe', 
                  color: '#1e40af',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }} 
              />
              <Chip 
                label="Pro tips included" 
                size="small"
                sx={{ 
                  bgcolor: '#dcfce7', 
                  color: '#166534',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }} 
              />
            </Box>
          </Paper>

          {/* Features */}
          <Box>
            <FeatureCard
              icon={<UserPlusIcon sx={{ fontSize: 28 }} />}
              title="1. Create Contact"
              description="Add new contacts to your database with essential information."
              color="#3b82f6"
              steps={[
                "Navigate to the Contacts section from the main menu",
                "Click the '+ Add Contact' button",
                "Fill in the contact details including name, email, phone number, and any additional information",
                "Click 'SAVE CONTACT' to add the contact to your system",
                "Your new contact will now appear in your contacts list"
              ]}
              tips={[
                "Double-check contact information before saving",
                "Use tags or categories to organize your contacts",
                "Add notes to remember important details about each contact"
              ]}
            />

            <FeatureCard
              icon={<Building2Icon sx={{ fontSize: 28 }} />}
              title="2. Create Client (Link Contact)"
              description="Convert contacts into clients and establish relationships."
              color="#8b5cf6"
              steps={[
                "Go to the Clients section in the main navigation",
                "Click '+ ADD CLIENT' to start the process",
                "Enter the client name and entity type and additional information through 'Show Additional Details'",
                "Click 'Save Client' to complete the client profile",
                "Your new client will now appear in your client management list",
                "Once your new client is created, you can view the client details by selecting the 'eye' icon",
                "In the client's page, under 'Linked Contact' you can link existing contacts by selecting 'ACTIONS' → 'Link Contact'",
                "Search the contact through email or name, select the Relationship Type and whether it is a Primary Contact",
                "Choose 'LINK CONTACT' to proceed with the linking"
              ]}
              tips={[
                "One client can have multiple linked contacts",
                "Update contact relationships as your business evolves",
                "Add company-specific information to keep track of client needs"
              ]}
            />

            <FeatureCard
              icon={<CalendarIcon sx={{ fontSize: 28 }} />}
              title="3. Setup Meeting"
              description="Schedule and manage meetings with your contacts and clients."
              color="#ec4899"
              steps={[
                "Access the Client's Detail page",
                "Click 'Schedule Meeting' button",
                "Select the date and time for your meeting",
                "Add participants by selecting Contacts or Advisors",
                "Include a meeting type and description/purpose",
                "Confirm by selecting 'Schedule Meeting'"
              ]}
              tips={[
                "Check participant availability before scheduling",
                "Send meeting agendas in advance for better preparation",
                "Use recurring meetings for regular check-ins"
              ]}
            />

            <FeatureCard
              icon={<MailIcon sx={{ fontSize: 28 }} />}
              title="4. Send Email"
              description="Communicate with contacts and clients directly from the application."
              color="#f59e0b"
              steps={[
                "Open the Client Details page",
                "Click on the fab icon on the bottom right hand side, choose 'EMAIL'",
                "Once Outlook is connected you can select the recipients with your contacts integrated",
                "Write your subject line and email message with AI and graph access through slash commands (i.e. /assets, /liabilities)",
                "Click 'Send' to deliver your message",
                "All emails sent here are saved to files page and in emails page for traceability. Replies to sent emails are also visible through email page"
              ]}
              tips={[
                "Save frequently used emails as templates",
                "Track email opens and responses if the feature is enabled",
                "Use professional email signatures to maintain brand consistency"
              ]}
            />
          </Box>

          {/* Support Section */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mt: 2, 
              mb: 3,
              borderRadius: 2,
              bgcolor: '#eff6ff',
              border: '1px solid #bfdbfe'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e40af', fontSize: '1rem' }}>
              Need More Help?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2.5, color: '#1e40af' }}>
              If you have questions or need additional assistance, our support team is here to help!
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button 
                variant="contained" 
                size="medium"
                onClick={handleContactSupport}
                endIcon={<ArrowRightIcon />}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  bgcolor: '#1e40af',
                  '&:hover': {
                    bgcolor: '#1e3a8a'
                  },
                  borderRadius: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Contact Support
              </Button>
              <Button 
                variant="outlined" 
                size="medium"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  bgcolor: 'white',
                  color: '#1e40af',
                  borderColor: '#3b82f6',
                  '&:hover': {
                    bgcolor: '#eff6ff',
                    borderColor: '#1e40af'
                  },
                  borderRadius: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                View Video Tutorials
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Manual;